<script setup lang="ts">
  import type { ComputedRef, Ref } from 'vue';
  import { computed, inject, ref } from 'vue';
  import { useTheme } from 'vuetify';

  import { useI18 } from '@/plugins';
  import type {
    headerItem,
    switchAccountItem,
  } from '@/submodules/himam-common-components/src/helpers/interfaces/header-item.interface';

  import ToggleTheme from '../../ToggleTheme.vue';

  import LanguageToggle from './LanguageToggle.vue';

  const profileImage: ComputedRef<string> | undefined = inject(
    'profileImage',
    undefined
  );
  const userFirstName: ComputedRef<string> | undefined =
    inject('userFirstName');
  const userLastName: ComputedRef<string> | undefined = inject('userLastName');
  const userEmail: ComputedRef<string> | undefined = inject('userEmail');
  const logoutText: Ref<string> = inject('logoutText', ref('Logout'));
  const profileItems: ComputedRef<headerItem[]> | undefined =
    inject('profileItems');
  const logout: () => void = inject('logout', () => undefined);

  const switchAccountItems: Ref<switchAccountItem[]> = inject(
    'switchAccountItems',
    ref([])
  );
  const modifiedSwitchAccountItems: ComputedRef<switchAccountItem[]> = computed(
    () => switchAccountItems.value.filter((item) => !item.disabled)
  );
  const theme = useTheme();
  const { t } = useI18();
</script>

<template>
  <v-menu :open-on-click="true" transition="slide-y-transition">
    <template #activator="{ props }">
      <v-avatar
        role="button"
        color="primary"
        :image="profileImage"
        class="unselectable"
        v-bind="props"
        aria-label="User avatar image"
      >
        {{ userFirstName?.[0]?.toUpperCase()
        }}{{ userLastName?.[0]?.toUpperCase() }}
      </v-avatar>
    </template>
    <v-card width="270px" rounded="lg">
      <v-list-item
        class="pa-4"
        :title="userFirstName + ' ' + userLastName"
        :active="false"
        @click.stop
      >
        <template #prepend>
          <v-avatar
            color="primary"
            :image="profileImage"
            class="unselectable cursor-pointer"
            aria-label="User avatar image"
          >
            {{ userFirstName?.[0]?.toUpperCase()
            }}{{ userLastName?.[0]?.toUpperCase() }}
          </v-avatar>
        </template>
        <template #subtitle>
          <p class="text-primary opacity-100">
            {{ userEmail }}
          </p>
        </template>
      </v-list-item>
      <v-divider></v-divider>

      <template v-if="modifiedSwitchAccountItems.length">
        <v-list>
          <v-list-item
            v-for="item in modifiedSwitchAccountItems"
            :key="item.title"
            role="option"
            base-color="gray"
            :to="item.to"
            :title="item.title"
            @click="item.fun"
          />
        </v-list>
        <v-divider></v-divider>
      </template>

      <v-list class="ma-0">
        <v-list-item
          v-for="item in profileItems"
          :key="item.title"
          role="option"
          base-color="gray"
          :to="item.to"
          :title="item.title"
        >
          <template #prepend>
            <div class="me-3">
              <v-icon :icon="item.icon"> </v-icon>
            </div>
          </template>
        </v-list-item>
        <v-divider class="my-2"></v-divider>
        <v-list-item
          :title="t('menu.language')"
          base-color="gray"
          aria-label="language"
          role="menuitem"
        >
          <template #append>
            <div class="text-black">
              <LanguageToggle />
            </div>
          </template>
        </v-list-item>
        <v-list-item
          :title="t('menu.theme')"
          base-color="gray"
          aria-label="theme"
          role="menuitem"
        >
          <template #prepend>
            <div class="me-3">
              <v-icon
                :icon="
                  theme.global.current.value.dark
                    ? 'mdi-weather-night'
                    : 'mdi-weather-sunny'
                "
              >
              </v-icon>
            </div>
          </template>
          <template #append>
            <div class="text-black">
              <ToggleTheme show-as-menu />
            </div>
          </template>
        </v-list-item>
        <!-- <v-list-item
          role="option"
          base-color="gray"
          :to="EnumRoutes.profile.settings.path"
          :title="EnumRoutes.profile.settings.title"
          :prepend-icon="EnumRoutes.profile.settings.icon"
        ></v-list-item>
        <v-list-item
          role="option"
          base-color="gray"
          :to="EnumRoutes.profile.helpCenter.path"
          :title="EnumRoutes.profile.helpCenter.title"
          :prepend-icon="EnumRoutes.profile.helpCenter.icon"
        ></v-list-item> -->
        <v-divider class="my-2"></v-divider>
        <v-list-item
          prepend-icon="mdi-logout"
          role="option"
          base-color="gray"
          :title="logoutText"
          @click="logout"
        ></v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>
